import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { Button, Card, CardContent, CardDescription, CardHeader } from "@authe/ui-core";
import { ROUTES } from "@/config";
import type { selectQuickStart, QuickStartAction } from "./quickStartModel";

export function QuickStartActionCard({ action }: { action: QuickStartAction }): React.ReactElement {
  return <div className="flex min-w-0 flex-col items-start rounded-lg border bg-background p-4">
    <h3 className="text-sm font-semibold">{action.title}</h3>
    <p title={action.description} className="mb-4 mt-1 line-clamp-3 w-full min-w-0 text-sm text-muted-foreground [overflow-wrap:anywhere]">{action.description}</p>
    <Button asChild variant="secondary" size="sm" className="mt-auto h-auto min-h-8 max-w-full whitespace-normal text-left">
      <Link to={action.to}>{action.cta}<ArrowRight aria-hidden="true" className="size-4 shrink-0" /></Link>
    </Button>
  </div>;
}

/** Shared presentation for home, the full guide and the isolated UI fixture. */
export function QuickStartCard({ model, showGuideLink = false, minimized = false, onMinimizedChange }: {
  model: ReturnType<typeof selectQuickStart>;
  showGuideLink?: boolean;
  minimized?: boolean;
  onMinimizedChange?: (value: boolean) => void;
}): React.ReactElement {
  const contentId = React.useId();
  return <Card className="border-primary/20 bg-primary/5">
    <CardHeader>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Quick start</h2>
        <div className="flex flex-wrap gap-2">
          {showGuideLink && <Button asChild variant="outline" size="sm"><Link to={ROUTES.gettingStarted}>View the full guide<ArrowRight aria-hidden="true" className="size-4" /></Link></Button>}
          {onMinimizedChange && <Button variant="ghost" size="sm" aria-expanded={!minimized} aria-controls={contentId} onClick={() => onMinimizedChange(!minimized)}>
            {minimized ? "Expand guide" : "Minimize guide"}
            {minimized ? <ChevronDown aria-hidden="true" className="size-4" /> : <ChevronUp aria-hidden="true" className="size-4" />}
          </Button>}
        </div>
      </div>
      {!minimized && <CardDescription>{model.description}</CardDescription>}
    </CardHeader>
    <CardContent id={contentId}>
      {minimized ? <nav aria-label="Quick-start shortcuts" className="flex flex-wrap gap-2">
        {model.recommended.map(action => <Button key={action.id} asChild size="sm" variant="secondary" className="h-auto min-h-8 max-w-full whitespace-normal"><Link to={action.to}>{action.cta}</Link></Button>)}
        {model.recommended.length === 0 && <p className="text-sm text-muted-foreground">{model.description}</p>}
      </nav> : <div className="grid gap-3 xl:grid-cols-3">
        {model.recommended.map(action => <QuickStartActionCard key={action.id} action={action} />)}
      </div>}
    </CardContent>
  </Card>;
}
